/**
 * Livery: a `Loadout` turned into a car.
 *
 * This is the seam between the ECONOMY and the RENDERER. src/game/cosmetics.ts
 * says an item is a `generator` name plus `params` and never a file path;
 * src/game/items.ts names the generators; this file is where those names are
 * cashed in. Every slot that shows up on the car — body, paint finish, decal,
 * wheels, boost, trail, topper, antenna, number, flag — resolves through one of
 * the registries below or through ./decals, and a generator that the catalogue
 * names and the renderer does not implement is an ERROR rather than a car that
 * quietly renders with nothing on it. See `missingGenerators`, which
 * test/render/livery.test.ts runs over the whole catalogue.
 *
 * ===========================================================================
 * WHAT IS DRAWN, WHAT IS BUILT, WHAT IS TUNED
 * ===========================================================================
 *
 * Four mechanisms, and which one a slot uses is not arbitrary:
 *
 *   TEXTURE   decal, number, flag, primary and accent paint. These are marks on
 *             a surface that already exists, so they are composited into ONE
 *             RGBA map in the loft's own UV space (./decals) and uploaded once.
 *             One texture per distinct appearance, shared by every car wearing
 *             it, never rebuilt per frame.
 *
 *   GEOMETRY  body, wheels, topper, antenna. These change the silhouette, and a
 *             silhouette cannot be painted on. Each is a small procedural mesh
 *             cached by its recipe, so six cars on identical wheels upload one
 *             set of wheel geometry between them.
 *
 *   MATERIAL  the paint finish. The whole point of it is that matte and metallic
 *             answer a floodlight differently, which is neither a colour nor a
 *             shape. See `PAINT_FINISHES` in ./materials.
 *
 *   PARTICLES boost and trail, which resolve to plain style objects that
 *             `EffectsSystem.setCarStyle` consumes.
 *
 * ===========================================================================
 * SHARING
 * ===========================================================================
 *
 * Six customized cars render at once, and most of what they are made of does not
 * depend on the loadout at all — the shell, the canopy, the aero, the lamps are
 * a function of the BODY. Those are cached by body id and shared outright. What
 * does depend on the loadout is cached by a SIGNATURE of the loadout, so two
 * players in the same colours with the same wheels cost one of everything, and
 * two players in different colours cost two. The material and texture caches
 * reference count, because a match ending has to give the GPU memory back and a
 * match ending while another match is up (watch-live) must not.
 *
 * NOTHING HERE TOUCHES WEBGL. `DataTexture` and `BufferGeometry` are typed
 * arrays until a renderer uploads them, so every function in this file runs in
 * Node and a livery can be measured in a test.
 */

import {
  BoxGeometry,
  BufferGeometry,
  ClampToEdgeWrapping,
  ConeGeometry,
  CylinderGeometry,
  DataTexture,
  IcosahedronGeometry,
  LatheGeometry,
  LinearFilter,
  LinearMipmapLinearFilter,
  RGBAFormat,
  RepeatWrapping,
  SRGBColorSpace,
  SphereGeometry,
  TorusGeometry,
  Vector2,
} from 'three';
import type { CosmeticSlot, ItemDef, Loadout, PaintColor } from '../game/cosmetics';
import {
  DECAL_GENERATORS,
  FLAG_GENERATORS,
  NUMBER_GENERATORS,
  SKIN,
  SKIN_DOOR,
  bool,
  buildSkinMap,
  colorParam,
  luminance,
  num,
  numberDigitsOf,
  shadeHex,
  skinTextureData,
  str,
  type Decal,
  type PanelPainter,
  type SkinPanel,
  type SkinParams,
} from './decals';
import {
  CAR_BODY_IDS,
  carBodySpec,
  mergeGeometries,
  sampleStation,
  type CarBodyId,
} from './car-body';
import { PAINT_FINISHES, TEAM_COLORS, defaultFinish, type PaintFinish } from './materials';

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

// ---------------------------------------------------------------------------
// Wheels
// ---------------------------------------------------------------------------

/**
 * A tyre cross-section, revolved about the axle, in units of the tyre's own
 * radius and half-width. One profile serves every wheel on both cars because
 * the numbers are RATIOS — which is also why a rim style can never change what
 * `car-config` says a wheel's radius is.
 */
export type TyreProfile = readonly (readonly [number, number])[];

export const TYRE_PROFILES: Readonly<Record<string, TyreProfile>> = {
  /** The stock road tyre: bead, sidewall bulge, shoulder radius, flat tread. */
  road: [
    [0.63, -0.5],
    [0.82, -0.5],
    [0.95, -0.4],
    [1.0, -0.28],
    [1.0, 0.28],
    [0.95, 0.4],
    [0.82, 0.5],
    [0.63, 0.5],
  ],
  /** A slick: almost no sidewall crown, square shoulders, wider footprint. */
  slick: [
    [0.66, -0.52],
    [0.9, -0.52],
    [1.0, -0.46],
    [1.0, 0.46],
    [0.9, 0.52],
    [0.66, 0.52],
  ],
  /** Off-road: a tall balloon sidewall and a stepped shoulder block. */
  knobby: [
    [0.6, -0.46],
    [0.86, -0.5],
    [0.96, -0.5],
    [0.96, -0.42],
    [1.0, -0.36],
    [1.0, 0.36],
    [0.96, 0.42],
    [0.96, 0.5],
    [0.86, 0.5],
    [0.6, 0.46],
  ],
};

export const TYRE_PROFILE_NAMES: readonly string[] = Object.freeze(
  Object.keys(TYRE_PROFILES).sort()
);

/**
 * A rim, as the parts that make it up.
 *
 * Everything is expressed as a multiple of `radius` and `width`, so a style can
 * never contradict the per-car wheel table in src/physics/car-config.ts: the
 * generator decides what the face LOOKS like and the axle decides how big it is.
 * That is pinned by a test, because a style that quietly grew a tyre would put
 * the contact patch under the floor and the car would look like it was sinking.
 *
 * `side` is +1 for the car's right and -1 for its left; only the outboard face
 * is built, because the inboard one lives inside a wheel well.
 */
export type RimGenerator = (
  radius: number,
  width: number,
  side: 1 | -1,
  params: SkinParams | undefined
) => BufferGeometry[];

/** The lip every style wears, at the outer edge of the tyre. */
const rimLip = (radius: number, width: number, side: 1 | -1, thickness: number): BufferGeometry => {
  const ring = new TorusGeometry(radius * 0.7, radius * 0.075 * thickness, 4, 16);
  ring.rotateY(Math.PI / 2);
  ring.translate(side * width * 0.44, 0, 0);
  return ring;
};

/** The centre cap, standing proud of the face so it catches its own highlight. */
const rimHub = (radius: number, width: number, side: 1 | -1, scale = 1): BufferGeometry => {
  const hub = new CylinderGeometry(radius * 0.26 * scale, radius * 0.3 * scale, width * 0.34, 10);
  hub.rotateZ(Math.PI / 2);
  hub.translate(side * width * 0.36, 0, 0);
  return hub;
};

/**
 * Where the spoke face sits, inboard of the lip. This is the DISH, and it is the
 * difference between a wheel and a hubcap: with the spokes flush to the lip
 * there is no depth for a highlight to travel across and the whole face reads as
 * one flat disc at chase distance.
 */
const faceOffset = (width: number, side: 1 | -1, dish: number): number =>
  side * width * (0.44 - clamp(dish, -0.15, 0.4));

export const RIM_GENERATORS: Readonly<Record<string, RimGenerator>> = {
  /**
   * Straight spokes on a dished face. The workhorse: `spokes` from three to ten
   * and `dish` from flat to deep covers most of a real wheel catalogue.
   *
   * A box is TWO spokes, so half as many boxes as spokes — and an odd count gets
   * the extra one as a half-length arm, because a five-spoke wheel with six
   * spokes on it is the kind of error nobody sees and everybody feels.
   */
  rimSpoke: (radius, width, side, params) => {
    const spokes = Math.max(3, Math.round(num(params, 'spokes', 6)));
    const thickness = num(params, 'thickness', 0.3);
    const faceX = faceOffset(width, side, num(params, 'dish', 0.18));
    const parts = [rimLip(radius, width, side, num(params, 'lip', 1))];
    const pairs = Math.floor(spokes / 2);
    for (let i = 0; i < pairs; i++) {
      const spoke = new BoxGeometry(width * thickness, radius * 1.3, radius * 0.3);
      spoke.rotateX((i * Math.PI * 2) / spokes);
      spoke.translate(faceX, 0, 0);
      parts.push(spoke);
    }
    if (spokes % 2 === 1) {
      const spoke = new BoxGeometry(width * thickness, radius * 0.65, radius * 0.3);
      spoke.translate(0, radius * 0.325, 0);
      spoke.rotateX((pairs * Math.PI * 2) / spokes);
      spoke.translate(faceX, 0, 0);
      parts.push(spoke);
    }
    parts.push(rimHub(radius, width, side));
    return parts;
  },

  /**
   * A mesh rim: two counter-rotated sets of thin bars. Reads as a woven face at
   * any distance where the bars are still separable, and as a darker, busier
   * disc past that — which is what a real mesh wheel does.
   */
  rimMesh: (radius, width, side, params) => {
    const spokes = Math.max(4, Math.round(num(params, 'spokes', 10)));
    const faceX = faceOffset(width, side, num(params, 'dish', 0.14));
    const parts = [rimLip(radius, width, side, num(params, 'lip', 1.1))];
    for (const skew of [0.22, -0.22]) {
      for (let i = 0; i < Math.max(1, Math.round(spokes / 2)); i++) {
        const bar = new BoxGeometry(width * 0.12, radius * 1.34, radius * 0.12);
        bar.rotateX((i * Math.PI * 2) / spokes + skew);
        bar.translate(faceX, 0, 0);
        parts.push(bar);
      }
    }
    parts.push(rimHub(radius, width, side, 0.8));
    return parts;
  },

  /**
   * A solid dish — a moon disc with a rolled edge. The one style with no gaps,
   * so it is the only one whose face carries a highlight all the way across.
   * `cone` is how far the face stands proud of the barrel.
   */
  rimDish: (radius, width, side, params) => {
    const cone = clamp(num(params, 'cone', 0.12), 0, 0.5);
    const parts = [rimLip(radius, width, side, num(params, 'lip', 1.3))];
    const face = new CylinderGeometry(radius * 0.7, radius * 0.62, width * cone + width * 0.06, 24);
    face.rotateZ(Math.PI / 2);
    face.translate(side * width * (0.44 - cone / 2 - 0.03), 0, 0);
    parts.push(face);
    parts.push(rimHub(radius, width, side, 1.1));
    return parts;
  },

  /**
   * A turbine: many shallow, raked vanes. Rotationally busy on purpose — a wheel
   * of revolution looks stationary however fast it turns, and the rake is what
   * gives the eye something to track.
   */
  rimTurbine: (radius, width, side, params) => {
    const vanes = Math.max(5, Math.round(num(params, 'spokes', 12)));
    const rake = num(params, 'rake', 0.42);
    const faceX = faceOffset(width, side, num(params, 'dish', 0.1));
    const parts = [rimLip(radius, width, side, num(params, 'lip', 0.9))];
    for (let i = 0; i < vanes; i++) {
      const vane = new BoxGeometry(width * 0.2, radius * 0.62, radius * 0.2);
      vane.rotateZ(side * rake);
      vane.translate(0, radius * 0.36, 0);
      vane.rotateX((i * Math.PI * 2) / vanes);
      vane.translate(faceX, 0, 0);
      parts.push(vane);
    }
    parts.push(rimHub(radius, width, side, 0.9));
    return parts;
  },

  /**
   * Split spokes: pairs forking from the hub to the lip. The most "aftermarket
   * alloy" of the set, and the one that most changes a car's read at rest.
   */
  rimSplit: (radius, width, side, params) => {
    const pairs = Math.max(3, Math.round(num(params, 'spokes', 5)));
    const spread = num(params, 'spread', 0.17);
    const faceX = faceOffset(width, side, num(params, 'dish', 0.2));
    const parts = [rimLip(radius, width, side, num(params, 'lip', 1.15))];
    for (let i = 0; i < pairs; i++) {
      for (const sign of [1, -1] as const) {
        const arm = new BoxGeometry(width * 0.17, radius * 0.7, radius * 0.16);
        arm.rotateZ(sign * spread);
        arm.translate(0, radius * 0.38, 0);
        arm.rotateX((i * Math.PI * 2) / pairs);
        arm.translate(faceX, 0, 0);
        parts.push(arm);
      }
    }
    parts.push(rimHub(radius, width, side, 1.05));
    return parts;
  },

  /**
   * A star: a deep-dished face with long tapered points. Fewer, bigger shapes
   * than any other style, which is what makes it legible from the stands.
   */
  rimStar: (radius, width, side, params) => {
    const points = Math.max(4, Math.round(num(params, 'spokes', 5)));
    const faceX = faceOffset(width, side, num(params, 'dish', 0.28));
    const parts = [rimLip(radius, width, side, num(params, 'lip', 1.25))];
    for (let i = 0; i < points; i++) {
      const point = new ConeGeometry(radius * 0.2, radius * 1.3, 3);
      point.rotateZ(Math.PI / 2);
      point.rotateX(Math.PI / 2);
      point.translate(0, radius * 0.36, 0);
      point.rotateX((i * Math.PI * 2) / points);
      point.translate(faceX, 0, 0);
      parts.push(point);
    }
    parts.push(rimHub(radius, width, side, 1.2));
    return parts;
  },
};

export const RIM_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(RIM_GENERATORS).sort()
);

// ---------------------------------------------------------------------------
// Toppers and antennas
// ---------------------------------------------------------------------------

/**
 * Which of the car's materials draws one piece of an attachment.
 *
 * Named by ROLE rather than by colour so a topper does not have to know what a
 * paint finish is: `painted` takes the item's own paint (or the accent colour),
 * `metal` the rim material, `dark` the trim, `glow` the emissive accent.
 */
export type AttachmentMaterial = 'painted' | 'metal' | 'dark' | 'glow';

export interface AttachmentPart {
  geometry: BufferGeometry;
  material: AttachmentMaterial;
}

/**
 * Attachment geometry, authored in the MESH frame with the origin AT THE ANCHOR:
 *
 *     +X = the car's right     +Y = up     -Z = forward
 *
 * so a builder only has to know "up". `scale` is in uu and is the piece's
 * nominal height, which is what keeps a crown and a cone the same size on the
 * roof without either of them restating a number from the body table.
 */
export type AttachmentBuilder = (scale: number, params: SkinParams | undefined) => AttachmentPart[];

export const TOPPER_GENERATORS: Readonly<Record<string, AttachmentBuilder>> = {
  /** A traffic cone: the oldest joke in the game, and still the clearest read. */
  topperCone: (scale, params) => {
    const stripes = Math.max(0, Math.round(num(params, 'stripes', 1)));
    const body = new ConeGeometry(scale * 0.42, scale, 14, 1);
    body.translate(0, scale / 2, 0);
    const base = new BoxGeometry(scale * 1.02, scale * 0.12, scale * 1.02);
    base.translate(0, scale * 0.06, 0);
    const parts: AttachmentPart[] = [
      { geometry: body, material: 'painted' },
      { geometry: base, material: 'dark' },
    ];
    for (let i = 0; i < stripes; i++) {
      const at = 0.4 + (i * 0.42) / Math.max(1, stripes);
      const r = 0.42 * (1 - at) * scale;
      // A torus rather than an open cylinder, for the same reason the crown's
      // band is closed: a one-sided tube shows its own inside.
      const ring = new TorusGeometry(r * 1.02, scale * 0.045, 5, 16);
      ring.rotateX(Math.PI / 2);
      ring.translate(0, scale * at, 0);
      parts.push({ geometry: ring, material: 'metal' });
    }
    return parts;
  },

  /** A crown. Points on a band, and nothing else — it has to read as a shape. */
  topperCrown: (scale, params) => {
    const points = Math.max(3, Math.round(num(params, 'points', 6)));
    // Closed, not open-ended: a one-sided tube is see-through from the front,
    // and a crown you can look through the near wall of reads as a bug.
    const band = new CylinderGeometry(scale * 0.5, scale * 0.5, scale * 0.42, 16);
    band.translate(0, scale * 0.21, 0);
    const parts: AttachmentPart[] = [{ geometry: band, material: 'painted' }];
    const spikes: BufferGeometry[] = [];
    for (let i = 0; i < points; i++) {
      const spike = new ConeGeometry(scale * 0.11, scale * 0.5, 4);
      spike.translate(0, scale * 0.67, 0);
      const a = (i / points) * Math.PI * 2;
      spike.translate(Math.cos(a) * scale * 0.46, 0, Math.sin(a) * scale * 0.46);
      spikes.push(spike);
    }
    parts.push({ geometry: mergeGeometries(spikes), material: 'painted' });
    const jewel = new IcosahedronGeometry(scale * 0.16, 0);
    jewel.translate(0, scale * 0.5, 0);
    parts.push({ geometry: jewel, material: 'glow' });
    return parts;
  },

  /** A shark fin, raked back. The only topper that reads from directly behind. */
  topperFin: (scale, params) => {
    const rake = num(params, 'rake', 0.5);
    const fin = new ConeGeometry(scale * 0.5, scale * 1.1, 3);
    fin.rotateY(Math.PI / 2);
    fin.scale(0.24, 1, 1);
    fin.rotateX(-rake);
    // Seat it on its OWN bounding box rather than a hand-picked offset. A cone
    // is built centred, and raking it pushes the trailing corner further down
    // by an amount that depends on `rake` — a fixed translate was right for one
    // rake and sank the fin through the roof for every other. Measuring after
    // the rotation makes the seating correct for any rake.
    fin.computeBoundingBox();
    fin.translate(0, -(fin.boundingBox?.min.y ?? 0), scale * 0.1);
    return [{ geometry: fin, material: 'painted' }];
  },

  /** A rotating beacon: a dark base, a lens, and a cap. */
  topperBeacon: (scale) => {
    const base = new CylinderGeometry(scale * 0.36, scale * 0.42, scale * 0.24, 12);
    base.translate(0, scale * 0.12, 0);
    const lens = new CylinderGeometry(scale * 0.34, scale * 0.34, scale * 0.52, 12);
    lens.translate(0, scale * 0.5, 0);
    const cap = new SphereGeometry(scale * 0.34, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    cap.translate(0, scale * 0.76, 0);
    return [
      { geometry: base, material: 'dark' },
      { geometry: lens, material: 'glow' },
      { geometry: cap, material: 'metal' },
    ];
  },

  /** A halo, floating clear of the roof. Nothing supports it, deliberately. */
  topperHalo: (scale, params) => {
    const tilt = num(params, 'tilt', 0.22);
    const ring = new TorusGeometry(scale * 0.55, scale * 0.08, 8, 20);
    ring.rotateX(Math.PI / 2 + tilt);
    ring.translate(0, scale * 0.95, 0);
    return [{ geometry: ring, material: 'glow' }];
  },

  /** A stack of two dice, cornered. Small, dense, unmistakable in silhouette. */
  topperDice: (scale) => {
    const lower = new BoxGeometry(scale * 0.52, scale * 0.52, scale * 0.52);
    lower.rotateY(0.5);
    lower.translate(0, scale * 0.28, 0);
    const upper = new BoxGeometry(scale * 0.4, scale * 0.4, scale * 0.4);
    upper.rotateY(-0.35);
    upper.rotateX(0.3);
    upper.translate(0, scale * 0.74, 0);
    return [
      { geometry: lower, material: 'painted' },
      { geometry: upper, material: 'metal' },
    ];
  },
};

export const ANTENNA_GENERATORS: Readonly<Record<string, AttachmentBuilder>> = {
  /** A whip with a ball on the end. The shortest one in the set. */
  antennaBall: (scale, params) => {
    const radius = clamp(num(params, 'radius', 0.16), 0.05, 0.35);
    const mast = new CylinderGeometry(scale * 0.022, scale * 0.03, scale, 6);
    mast.translate(0, scale / 2, 0);
    const ball = new SphereGeometry(scale * radius, 10, 8);
    ball.translate(0, scale * (1 + radius * 0.6), 0);
    return [
      { geometry: mast, material: 'metal' },
      { geometry: ball, material: 'painted' },
    ];
  },

  /** A pennant: a triangular flag stiffened out to one side of the mast. */
  antennaPennant: (scale, params) => {
    const chord = clamp(num(params, 'chord', 0.6), 0.2, 1.2);
    const mast = new CylinderGeometry(scale * 0.022, scale * 0.03, scale, 6);
    mast.translate(0, scale / 2, 0);
    const cloth = new ConeGeometry(scale * 0.3, scale * chord, 3);
    cloth.rotateZ(-Math.PI / 2);
    cloth.scale(1, 1, 0.06);
    cloth.translate(scale * chord * 0.42, scale * 0.82, 0);
    return [
      { geometry: mast, material: 'metal' },
      { geometry: cloth, material: 'painted' },
    ];
  },

  /** A four-point star on a mast. Flat, so it flashes as the car turns. */
  antennaStar: (scale) => {
    const mast = new CylinderGeometry(scale * 0.022, scale * 0.03, scale * 0.92, 6);
    mast.translate(0, scale * 0.46, 0);
    const arms: BufferGeometry[] = [];
    for (const angle of [0, Math.PI / 2]) {
      const arm = new BoxGeometry(scale * 0.44, scale * 0.09, scale * 0.05);
      arm.rotateZ(angle);
      arms.push(arm);
    }
    const star = mergeGeometries(arms);
    star.translate(0, scale * 1.06, 0);
    return [
      { geometry: mast, material: 'metal' },
      { geometry: star, material: 'glow' },
    ];
  },

  /** A faceted gem on a short mast. The one antenna that reads as an object. */
  antennaGem: (scale) => {
    const mast = new CylinderGeometry(scale * 0.024, scale * 0.032, scale * 0.8, 6);
    mast.translate(0, scale * 0.4, 0);
    const gem = new IcosahedronGeometry(scale * 0.2, 0);
    gem.translate(0, scale * 0.96, 0);
    return [
      { geometry: mast, material: 'metal' },
      { geometry: gem, material: 'painted' },
    ];
  },

  /** A plain whip. No ornament at all, which is a choice a player may want. */
  antennaWhip: (scale) => {
    const mast = new CylinderGeometry(scale * 0.012, scale * 0.038, scale * 1.15, 6);
    mast.translate(0, scale * 0.575, 0);
    return [{ geometry: mast, material: 'metal' }];
  },
};

export const TOPPER_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(TOPPER_GENERATORS).sort()
);
export const ANTENNA_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(ANTENNA_GENERATORS).sort()
);

// ---------------------------------------------------------------------------
// Boost and trail
// ---------------------------------------------------------------------------

/**
 * How a boost looks and behaves. Consumed by `EffectsSystem.setCarStyle`.
 *
 * `buoyancy` is the one field worth explaining: it is the particle gravity, and
 * it is NEGATIVE for a plasma (which rises) and positive for sparks (which
 * fall). That single sign is most of the difference between an ion trail and a
 * shower of embers.
 */
export interface BoostStyle {
  /** Flame cone colour while cruising, and while supersonic. */
  core: number;
  supersonicCore: number;
  /** Trail particle colour at birth and at death. */
  hot: number;
  cool: number;
  /** Flame cone size multipliers. */
  length: number;
  width: number;
  /** Flicker rate, rad/s. */
  flicker: number;
  /** Trail particles per second, as a multiple of the stock rate. */
  rate: number;
  /** Trail particle size and how much it puffs out over its life. */
  size: number;
  growth: number;
  /** uu/s^2 applied to trail particles. Negative rises. */
  buoyancy: number;
  /** Trail particle lifetime, as a multiple of the stock one. */
  life: number;
}

export const STOCK_BOOST: BoostStyle = Object.freeze({
  core: 0xffb04a,
  supersonicCore: 0x9fd8ff,
  hot: 0xfff6dd,
  cool: 0xd63a12,
  length: 1,
  width: 1,
  flicker: 47,
  rate: 1,
  size: 1,
  growth: 2.4,
  buoyancy: -90,
  life: 1,
});

export type BoostGenerator = (params: SkinParams | undefined) => BoostStyle;

export const BOOST_GENERATORS: Readonly<Record<string, BoostGenerator>> = {
  /** The stock flame. Named so "Standard" is an item like any other. */
  boostFlame: (params) => ({
    ...STOCK_BOOST,
    core: colorParam(params, 'core') ?? STOCK_BOOST.core,
    hot: colorParam(params, 'hot') ?? STOCK_BOOST.hot,
    cool: colorParam(params, 'cool') ?? STOCK_BOOST.cool,
  }),

  /** A long, narrow, cold jet that rises. Reads as thrust rather than as fire. */
  boostPlasma: (params) => ({
    ...STOCK_BOOST,
    core: colorParam(params, 'core') ?? 0x6fd8ff,
    supersonicCore: shadeHex(colorParam(params, 'core') ?? 0x6fd8ff, 1.45),
    hot: colorParam(params, 'hot') ?? 0xeafaff,
    cool: colorParam(params, 'cool') ?? 0x1b6fd6,
    length: num(params, 'length', 1.45),
    width: num(params, 'width', 0.7),
    flicker: 90,
    rate: 1.15,
    size: 0.85,
    growth: 1.7,
    buoyancy: -160,
    life: 0.85,
  }),

  /** A shower of embers that FALLS. Short flame, dense trail, heavy particles. */
  boostSparks: (params) => ({
    ...STOCK_BOOST,
    core: colorParam(params, 'core') ?? 0xffd08a,
    supersonicCore: 0xfff0c8,
    hot: colorParam(params, 'hot') ?? 0xfff3c8,
    cool: colorParam(params, 'cool') ?? 0x8a1f05,
    length: num(params, 'length', 0.7),
    width: num(params, 'width', 1.15),
    flicker: 130,
    rate: 1.6,
    size: 0.55,
    growth: 0.7,
    buoyancy: 220,
    life: 1.3,
  }),

  /** A fat, slow, low-contrast plume. The quietest boost in the set. */
  boostSmoke: (params) => ({
    ...STOCK_BOOST,
    core: colorParam(params, 'core') ?? 0x9aa6b8,
    supersonicCore: 0xcfe0f4,
    hot: colorParam(params, 'hot') ?? 0xdfe7f2,
    cool: colorParam(params, 'cool') ?? 0x2a3140,
    length: num(params, 'length', 0.9),
    width: num(params, 'width', 1.5),
    flicker: 18,
    rate: 0.75,
    size: 1.7,
    growth: 3.4,
    buoyancy: -20,
    life: 1.8,
  }),

  /**
   * A hard, saturated single-colour jet — the one that reads as a team colour
   * from the far end of the pitch. Everything about it is derived from `core`,
   * so one number is the whole item.
   */
  boostIon: (params) => {
    const core = colorParam(params, 'core') ?? 0xc06bff;
    return {
      ...STOCK_BOOST,
      core,
      supersonicCore: shadeHex(core, 1.5),
      hot: shadeHex(core, 1.7),
      cool: shadeHex(core, 0.4),
      length: num(params, 'length', 1.2),
      width: num(params, 'width', 0.85),
      flicker: 60,
      rate: 1.1,
      size: 0.95,
      growth: 2,
      buoyancy: -110,
      life: 1,
    };
  },
};

export const BOOST_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(BOOST_GENERATORS).sort()
);

/**
 * A supersonic trail: colour, density and how long it hangs in the air.
 *
 * The renderer draws trails as particles, so a "ribbon" and a "streak" differ in
 * density and lifetime rather than in being a swept mesh. That is a real
 * limitation of the effects system and it is stated here rather than papered
 * over: a dense, long-lived, low-spread emission reads as a ribbon at speed,
 * which is the only speed a supersonic trail is ever seen at.
 */
export interface TrailStyle {
  hot: number;
  cool: number;
  /** Multiples of the stock emission rate, particle life and size. */
  rate: number;
  life: number;
  size: number;
}

export const STOCK_TRAIL: TrailStyle = Object.freeze({
  hot: 0xeaf4ff,
  cool: 0x2f6bd4,
  rate: 1,
  life: 1,
  size: 1,
});

export type TrailGenerator = (params: SkinParams | undefined) => TrailStyle;

export const TRAIL_GENERATORS: Readonly<Record<string, TrailGenerator>> = {
  /** The stock cold streak. */
  trailStreak: (params) => ({
    ...STOCK_TRAIL,
    hot: colorParam(params, 'hot') ?? STOCK_TRAIL.hot,
    cool: colorParam(params, 'cool') ?? STOCK_TRAIL.cool,
  }),
  /** Sparse, long-lived motes that hang where the car was. */
  trailEmber: (params) => ({
    hot: colorParam(params, 'hot') ?? 0xffe6b0,
    cool: colorParam(params, 'cool') ?? 0xb03a08,
    rate: 0.6,
    life: 2.1,
    size: 0.7,
  }),
  /** Dense and short — a hard ribbon rather than a cloud. */
  trailRibbon: (params) => ({
    hot: colorParam(params, 'hot') ?? 0xffffff,
    cool: colorParam(params, 'cool') ?? 0x7a3cff,
    rate: 2.2,
    life: 0.45,
    size: 1.35,
  }),
};

export const TRAIL_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(TRAIL_GENERATORS).sort()
);

// ---------------------------------------------------------------------------
// The resolved livery
// ---------------------------------------------------------------------------

/** One equipped item, already reduced to what the renderer needs. */
export interface GeneratorRef {
  readonly itemId: string;
  readonly generator: string;
  readonly params?: SkinParams;
  /** The painted variant's colour, already resolved through `PAINT_HEX`. */
  readonly paint: number | null;
}

/**
 * Everything about how one car looks. Flat, comparable, and reducible to a cache
 * key — which is the whole point: two cars with equal liveries share every
 * texture, geometry and material they are made of.
 */
export interface CarLivery {
  readonly body: CarBodyId;
  readonly team: 0 | 1;
  readonly primary: number;
  readonly accent: number;
  readonly finish: GeneratorRef | null;
  readonly decal: GeneratorRef | null;
  readonly number: GeneratorRef | null;
  readonly flag: GeneratorRef | null;
  readonly wheels: GeneratorRef | null;
  readonly boost: GeneratorRef | null;
  readonly trail: GeneratorRef | null;
  readonly topper: GeneratorRef | null;
  readonly antenna: GeneratorRef | null;
}

/** The stock car: team colours, stock everything, no attachments. */
export const stockLivery = (team: 0 | 1, body: CarBodyId = 'octane'): CarLivery => ({
  body,
  team,
  primary: TEAM_COLORS[team],
  accent: shadeHex(TEAM_COLORS[team], 1.55),
  finish: null,
  decal: null,
  number: null,
  flag: null,
  wheels: null,
  boost: null,
  trail: null,
  topper: null,
  antenna: null,
});

/** One slot's equipped item, as `resolvedItem` in src/game/inventory.ts returns it. */
export interface ResolvedCosmetic {
  readonly def: ItemDef;
  readonly paint?: PaintColor;
}

/**
 * What `resolveLivery` needs from the economy, expressed as functions so this
 * module depends on the CONTRACT (src/game/cosmetics.ts) and not on the
 * catalogue's implementation. src/game/items.ts supplies `paintHex`; the
 * profile's inventory supplies `slot`.
 */
export interface LiveryLookup {
  /** The item equipped in a slot for the team being resolved, or null. */
  slot(slot: CosmeticSlot): ResolvedCosmetic | null;
  /** `PAINT_HEX` from src/game/items.ts. */
  paintHex: Readonly<Partial<Record<PaintColor, number>>>;
}

/** A colour string from a `Loadout`, or the fallback when it is unusable. */
export const parseLoadoutColor = (value: string, fallback: number): number => {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(value.trim());
  return m ? parseInt(m[1], 16) : fallback;
};

const refFor = (lookup: LiveryLookup, slot: CosmeticSlot): GeneratorRef | null => {
  const resolved = lookup.slot(slot);
  if (!resolved) return null;
  const paint = resolved.paint;
  const hex = paint === undefined ? null : lookup.paintHex[paint];
  return {
    itemId: resolved.def.id,
    generator: resolved.def.generator,
    params: resolved.def.params,
    paint: typeof hex === 'number' ? hex : null,
  };
};

/**
 * The body a loadout selects.
 *
 * The body slot is special and is the reason `resolveLivery` cannot be a plain
 * map: the body is not a thing drawn ON the car, it is which car, and only two
 * of `CAR_BODIES` have art (`CAR_BODY_IDS`). An equipped body the renderer has
 * no loft for falls back to the Octane for the same reason `renderableBody` in
 * src/main.ts does — a car that is still there and still that hitbox, drawn as
 * something, beats a hole on the pitch.
 */
export const liveryBody = (lookup: LiveryLookup): CarBodyId => {
  const item = lookup.slot('body');
  if (!item) return 'octane';
  // The generator IS the body for a body item; `params.body` is accepted too,
  // because a catalogue is entitled to say it either way.
  const named =
    typeof item.def.params?.body === 'string' ? item.def.params.body : item.def.generator;
  return CAR_BODY_IDS.includes(named as CarBodyId) ? (named as CarBodyId) : 'octane';
};

export const resolveLivery = (
  loadout: Loadout,
  lookup: LiveryLookup,
  team: 0 | 1
): CarLivery => {
  const primary = parseLoadoutColor(loadout.primaryColor, TEAM_COLORS[team]);
  return {
    body: liveryBody(lookup),
    team,
    primary,
    accent: parseLoadoutColor(loadout.accentColor, shadeHex(primary, 1.55)),
    finish: refFor(lookup, 'paintFinish'),
    decal: refFor(lookup, 'decal'),
    number: refFor(lookup, 'number'),
    flag: refFor(lookup, 'flag'),
    wheels: refFor(lookup, 'wheels'),
    boost: refFor(lookup, 'boost'),
    trail: refFor(lookup, 'trail'),
    topper: refFor(lookup, 'topper'),
    antenna: refFor(lookup, 'antenna'),
  };
};

// ---------------------------------------------------------------------------
// Generator coverage
// ---------------------------------------------------------------------------

/**
 * Every generator name this renderer implements, by slot.
 *
 * The point of publishing it is the test in test/render/livery.test.ts: a
 * catalogue item naming a generator that is not in here draws NOTHING and
 * reports no error, which is the exact failure mode — an invisible cosmetic that
 * looks like a bug in the equip screen — that this table exists to turn into a
 * build failure.
 */
export const LIVERY_GENERATORS: Readonly<Partial<Record<CosmeticSlot, readonly string[]>>> =
  Object.freeze({
    body: CAR_BODY_IDS,
    decal: Object.freeze(Object.keys(DECAL_GENERATORS).sort()),
    paintFinish: Object.freeze(Object.keys(PAINT_FINISHES).sort()),
    wheels: RIM_GENERATOR_NAMES,
    boost: BOOST_GENERATOR_NAMES,
    trail: TRAIL_GENERATOR_NAMES,
    topper: TOPPER_GENERATOR_NAMES,
    antenna: ANTENNA_GENERATOR_NAMES,
    number: Object.freeze(Object.keys(NUMBER_GENERATORS).sort()),
    flag: Object.freeze(Object.keys(FLAG_GENERATORS).sort()),
  });

/** Slots this renderer is responsible for drawing. */
export const RENDERED_SLOTS: readonly CosmeticSlot[] = Object.freeze(
  Object.keys(LIVERY_GENERATORS) as CosmeticSlot[]
);

export const supportsGenerator = (slot: CosmeticSlot, generator: string): boolean =>
  (LIVERY_GENERATORS[slot] ?? []).includes(generator);

/**
 * Catalogue entries in a slot this renderer draws whose generator it does not
 * implement. Empty is the only acceptable answer; anything else is an item that
 * equips and does nothing.
 *
 * Bodies are exempt on purpose. `CAR_BODIES` in src/physics/car-config.ts has
 * seven hitboxes and ./car-body has art for two, which is a KNOWN gap the whole
 * render layer already handles by falling back to the Octane (`renderableBody`
 * in src/main.ts, `liveryBody` above). Failing the build over it would be
 * failing over a decision that was made deliberately and documented in three
 * places.
 */
export const missingGenerators = (
  items: readonly ItemDef[]
): { itemId: string; slot: CosmeticSlot; generator: string }[] =>
  items
    .filter(
      (item) =>
        RENDERED_SLOTS.includes(item.slot) &&
        item.slot !== 'body' &&
        !supportsGenerator(item.slot, item.generator)
    )
    .map((item) => ({ itemId: item.id, slot: item.slot, generator: item.generator }));

// ---------------------------------------------------------------------------
// Skin composition
// ---------------------------------------------------------------------------

/**
 * How big a door number and a flag badge are, and where they sit, in uu.
 *
 * In uu rather than in skin coordinates because the two bodies have different
 * perimeters: the same `s` band is several units taller on the Dominus, and a
 * number placed by index would be visibly bigger on one car than on the other.
 *
 * The two sit SIDE BY SIDE on the door, number forward and flag just behind it,
 * which is where a race car carries them. Further back is the rear arch — on the
 * Octane the wheel well reaches x = -27, and a badge half-cut by a wheel arch
 * reads as a mistake rather than as a badge.
 */
const NUMBER_PANEL = { width: 22, height: 15, t: 0.5 } as const;
const FLAG_PANEL = { width: 18, height: 12, t: 0.635 } as const;

/**
 * A colour for a mark on `body`, preferring `wanted` and falling back when the
 * two are too close to tell apart.
 *
 * This is what stops Titanium White paint from swallowing a white number and
 * black paint from swallowing a black one. It is a contrast test, not a tint:
 * the wanted colour is used unchanged whenever it is legible, so a red number on
 * a white car is still exactly red.
 */
export const contrastInk = (body: number, wanted: number): number => {
  const lb = luminance(body);
  const lw = luminance(wanted);
  if (Math.abs(lb - lw) > 0.22) return wanted;
  return lb > 0.5 ? 0x14181f : 0xf4f8ff;
};

/** Wrap a flag painter in a thin dark border, so it reads as a badge on paint. */
const borderedPanel = (inner: PanelPainter, border: number): PanelPainter => {
  return (x, y, out) => {
    if (x < 0.045 || x > 0.955 || y < 0.07 || y > 0.93) {
      out.color = border;
      out.coverage = 1;
      return;
    }
    inner((x - 0.045) / 0.91, (y - 0.07) / 0.86, out);
  };
};

/**
 * The panels a livery stamps on each flank, in the order they are drawn.
 *
 * Both sit on the flank between the arches, centred on the door line — the one
 * part of either body that is flat, unbroken and visible from a chase camera on
 * the other side of the pitch.
 */
export const liveryPanels = (livery: CarLivery): SkinPanel[] => {
  const map = buildSkinMap(livery.body);
  const doorArc = map.arcOfS(SKIN_DOOR);
  const panels: SkinPanel[] = [];

  if (livery.number) {
    const gen = NUMBER_GENERATORS[livery.number.generator];
    if (gen) {
      // A painted number takes its paint; otherwise it takes the accent, and if
      // that would vanish into the body it takes the body's own contrast shade.
      const ink = livery.number.paint ?? contrastInk(livery.primary, livery.accent);
      const trim = luminance(ink) > 0.5 ? shadeHex(livery.primary, 0.3) : 0xf2f6ff;
      const digits = numberDigitsOf(livery.number.params);
      panels.push({
        paint: gen(digits, { ink, trim }, livery.number.params),
        t: NUMBER_PANEL.t,
        arc: doorArc + 1.5,
        // Three digits need a wider panel or they set at half the height.
        width: NUMBER_PANEL.width * (digits.length >= 3 ? 1.3 : 1),
        height: NUMBER_PANEL.height,
      });
    }
  }

  if (livery.flag) {
    const gen = FLAG_GENERATORS[livery.flag.generator];
    if (gen) {
      // Switzerland is square and its item says so; everything else is 3:2.
      const square = bool(livery.flag.params, 'square', false);
      panels.push({
        paint: borderedPanel(gen(livery.flag.params), shadeHex(livery.primary, 0.34)),
        t: FLAG_PANEL.t,
        arc: doorArc + 3,
        width: square ? FLAG_PANEL.height : FLAG_PANEL.width,
        height: FLAG_PANEL.height,
      });
    }
  }

  return panels;
};

/** The decal for a livery, or null when nothing is equipped. */
export const liveryDecal = (livery: CarLivery): Decal | null => {
  if (!livery.decal) return null;
  const gen = DECAL_GENERATORS[livery.decal.generator];
  if (!gen) return null;
  const built = gen(livery.decal.params);
  // A painted decal IS that colour — a painted item in Rocket League is not a
  // tint of the original, it is the original in that paint.
  return livery.decal.paint === null ? built : { ...built, accent: livery.decal.paint };
};

/** The material parameters a livery's finish asks for. */
export const finishFor = (livery: CarLivery): PaintFinish =>
  (livery.finish ? PAINT_FINISHES[livery.finish.generator] : undefined) ?? defaultFinish();

/**
 * The signature of everything that goes into a body texture.
 *
 * Only the fields the texture depends on: two cars in the same colours with the
 * same decal share one map even if their wheels, boost and toppers all differ,
 * which is the point of keying on this rather than on the whole livery.
 */
export const skinKey = (livery: CarLivery): string => {
  const ref = (r: GeneratorRef | null): string =>
    r === null ? '-' : `${r.generator}:${r.paint ?? '-'}:${JSON.stringify(r.params ?? {})}`;
  return [
    livery.body,
    livery.primary.toString(16),
    livery.accent.toString(16),
    ref(livery.decal),
    ref(livery.number),
    ref(livery.flag),
  ].join('|');
};

/**
 * True when a livery paints nothing the stock team material does not already.
 *
 * A stock car takes NO skin map at all: the material's own colour is the team
 * colour, which keeps the default car byte-identical to what it was before
 * loadouts existed and keeps six stock cars off six megabytes of texture.
 */
export const isStockSkin = (livery: CarLivery): boolean =>
  livery.decal === null &&
  livery.number === null &&
  livery.flag === null &&
  livery.primary === TEAM_COLORS[livery.team];

// ---------------------------------------------------------------------------
// Caches
// ---------------------------------------------------------------------------

interface Entry<T> {
  value: T;
  refs: number;
}

/**
 * Reference-counted shared resource.
 *
 * A plain cache would be wrong in both directions here: never releasing leaks a
 * megabyte per livery the player browses past in the garage, and releasing on
 * the first `dispose` frees a texture six cars are still drawing with. Counting
 * is the only version that survives a rematch starting while a watch-live match
 * is still up.
 */
class SharedCache<T> {
  private readonly entries = new Map<string, Entry<T>>();

  constructor(private readonly destroy: (value: T) => void) {}

  acquire(key: string, build: () => T): T {
    const existing = this.entries.get(key);
    if (existing) {
      existing.refs++;
      return existing.value;
    }
    const value = build();
    this.entries.set(key, { value, refs: 1 });
    return value;
  }

  release(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    entry.refs--;
    if (entry.refs > 0) return;
    this.entries.delete(key);
    this.destroy(entry.value);
  }

  /** Live entries, for the render-cost report and for tests. */
  get size(): number {
    return this.entries.size;
  }

  refs(key: string): number {
    return this.entries.get(key)?.refs ?? 0;
  }
}

const skinTextures = new SharedCache<DataTexture>((t) => t.dispose());

/** Texture resolution. 512 is where a three-digit number stops looking soft. */
export const SKIN_TEXTURE_SIZE = 512;

/**
 * The body map for a livery. Browser and Node both — `DataTexture` is a typed
 * array until something uploads it.
 */
export const acquireSkinTexture = (livery: CarLivery, size = SKIN_TEXTURE_SIZE): DataTexture =>
  skinTextures.acquire(skinKey(livery), () => {
    const data = skinTextureData(
      {
        body: livery.body,
        primary: livery.primary,
        accent: livery.accent,
        decal: liveryDecal(livery),
        panels: liveryPanels(livery),
      },
      size
    );
    const texture = new DataTexture(data, size, size, RGBAFormat);
    texture.colorSpace = SRGBColorSpace;
    // u wraps around the section and v runs nose to tail: one repeats, the other
    // must not, or the nose samples the tail.
    texture.wrapS = RepeatWrapping;
    texture.wrapT = ClampToEdgeWrapping;
    texture.minFilter = LinearMipmapLinearFilter;
    texture.magFilter = LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = 8;
    texture.needsUpdate = true;
    return texture;
  });

export const releaseSkinTexture = (livery: CarLivery): void => skinTextures.release(skinKey(livery));

/** Live skin textures. Six identically-liveried cars must report 1. */
export const skinTextureCount = (): number => skinTextures.size;

export const skinTextureRefs = (livery: CarLivery): number => skinTextures.refs(skinKey(livery));

// --- geometry ---------------------------------------------------------------

const sharedGeometries = new Map<string, BufferGeometry>();

/**
 * A geometry every car with the same recipe shares.
 *
 * Marked `userData.shared`, and ./car-mesh skips those when a car is disposed:
 * a shell freed because one of six cars went away is a shell the other five have
 * to re-upload. These are bounded by the number of bodies and equipped wheel
 * styles, so they are simply kept — the same trade `sharedCarDetailTexture`
 * makes.
 */
export const sharedGeometry = (key: string, build: () => BufferGeometry): BufferGeometry => {
  const existing = sharedGeometries.get(key);
  if (existing) return existing;
  const geometry = build();
  geometry.userData.shared = true;
  sharedGeometries.set(key, geometry);
  return geometry;
};

export const sharedGeometryCount = (): number => sharedGeometries.size;

/** Drop every shared geometry. Tests only — a running game shares for its life. */
export const clearSharedGeometry = (): void => {
  for (const geometry of sharedGeometries.values()) geometry.dispose();
  sharedGeometries.clear();
};

// ---------------------------------------------------------------------------
// Wheel and attachment assembly
// ---------------------------------------------------------------------------

/** The tyre profile a wheel item asks for, defaulting to the road tyre. */
export const tyreProfileNameFor = (livery: CarLivery): string => {
  const name = str(livery.wheels?.params, 'tyre', 'road');
  return name in TYRE_PROFILES ? name : 'road';
};

export const tyreProfileFor = (livery: CarLivery): TyreProfile =>
  TYRE_PROFILES[tyreProfileNameFor(livery)];

/** Key for one built wheel: everything that changes its geometry, and nothing else. */
export const wheelKey = (livery: CarLivery, front: boolean, side: 1 | -1): string => {
  const ref = livery.wheels;
  const params = JSON.stringify(ref?.params ?? {});
  return `wheel|${livery.body}|${front ? 'f' : 'b'}|${side}|${ref?.generator ?? 'rimSpoke'}|${params}`;
};

/** The merged, shared rim geometry for one wheel of one livery. */
export const rimGeometryFor = (
  livery: CarLivery,
  radius: number,
  width: number,
  front: boolean,
  side: 1 | -1
): BufferGeometry =>
  sharedGeometry(wheelKey(livery, front, side), () => {
    const gen = RIM_GENERATORS[livery.wheels?.generator ?? ''] ?? RIM_GENERATORS.rimSpoke;
    return mergeGeometries(gen(radius, width, side, livery.wheels?.params));
  });

/** The tyre lathe for one wheel, shared across every car wearing the same tyre. */
export const tyreGeometryFor = (
  livery: CarLivery,
  radius: number,
  width: number
): BufferGeometry => {
  const name = tyreProfileNameFor(livery);
  return sharedGeometry(`tyre|${name}|${radius}|${width}`, () => {
    const geometry = new LatheGeometry(
      TYRE_PROFILES[name].map(([r, w]) => new Vector2(r * radius, w * width)),
      20
    );
    geometry.rotateZ(Math.PI / 2);
    return geometry;
  });
};

/**
 * Where a topper and an antenna are planted, in the CAR frame.
 *
 * Both are derived from the body's own station table rather than typed in, so a
 * roof lowered by two units carries its topper down with it. The topper goes on
 * the middle of the painted roof panel and the antenna on the rear deck behind
 * it, which is the only pair of spots on both bodies that is flat, clear of the
 * canopy, and not already occupied by a wing.
 */
export interface AttachmentAnchor {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Nominal height of the piece, uu. */
  readonly scale: number;
}

/** Clearance above the crown, matching the roof panel's own offset in ./car-mesh. */
const ROOF_LIFT = 1.6;

export const topperAnchor = (body: CarBodyId): AttachmentAnchor => {
  const spec = carBodySpec(body);
  const x = (spec.roof.front + spec.roof.back) / 2;
  return { x, y: 0, z: sampleStation(spec.stations, x).top + ROOF_LIFT, scale: 17 };
};

export const antennaAnchor = (body: CarBodyId): AttachmentAnchor => {
  const spec = carBodySpec(body);
  // Behind the roof panel but ahead of the wing, on the deck.
  const x = (spec.roof.back + spec.wing.x) / 2;
  return { x, y: 0, z: sampleStation(spec.stations, x).top + ROOF_LIFT * 0.5, scale: 26 };
};

/**
 * Build one attachment's parts, shared by recipe.
 *
 * One merged geometry per material role, so a six-piece crown is at most four
 * draws and usually two. Parts built only to be merged are disposed by
 * `mergeGeometries`; on a cache hit the freshly built ones are disposed instead,
 * because reproducing the key without running the builder would mean serializing
 * every parameter twice and a builder is cheap.
 */
export const attachmentParts = (
  registry: Readonly<Record<string, AttachmentBuilder>>,
  ref: GeneratorRef,
  anchor: AttachmentAnchor,
  kind: string
): AttachmentPart[] | null => {
  const builder = registry[ref.generator];
  if (!builder) return null;
  const params = JSON.stringify(ref.params ?? {});
  const raw = builder(anchor.scale, ref.params);
  const roles: AttachmentMaterial[] = ['painted', 'metal', 'dark', 'glow'];
  const out: AttachmentPart[] = [];
  for (const role of roles) {
    const parts = raw.filter((p) => p.material === role);
    if (parts.length === 0) continue;
    const key = `${kind}|${ref.generator}|${params}|${anchor.scale}|${role}`;
    const cached = sharedGeometries.get(key);
    if (cached) {
      for (const p of parts) p.geometry.dispose();
      out.push({ geometry: cached, material: role });
      continue;
    }
    out.push({
      geometry: sharedGeometry(key, () =>
        parts.length === 1 ? parts[0].geometry : mergeGeometries(parts.map((p) => p.geometry))
      ),
      material: role,
    });
  }
  return out;
};

// ---------------------------------------------------------------------------
// Colours the car's parts take from the livery
// ---------------------------------------------------------------------------

/**
 * The colour of a painted part of an attachment.
 *
 * Painted items take their paint; unpainted ones take the loadout's accent,
 * unless that would be invisible against the body — see `contrastInk`. This is
 * the same rule the number follows, stated once.
 */
export const attachmentColor = (livery: CarLivery, ref: GeneratorRef): number =>
  ref.paint ?? contrastInk(livery.primary, livery.accent);

/** Rim colour: the wheel item's paint, then its own `rimColor`, then chrome. */
export const rimColor = (livery: CarLivery, chrome: number): number =>
  livery.wheels?.paint ?? colorParam(livery.wheels?.params, 'rimColor') ?? chrome;

/** The boost style a livery flies, defaulting to the stock flame. */
export const boostStyleFor = (livery: CarLivery): BoostStyle => {
  const ref = livery.boost;
  if (!ref) return STOCK_BOOST;
  const gen = BOOST_GENERATORS[ref.generator];
  if (!gen) return STOCK_BOOST;
  const style = gen(ref.params);
  if (ref.paint === null) return style;
  // A painted boost recolours the whole flame rather than tinting it: a painted
  // item in Rocket League IS that colour, and a 30 % tint of Titanium White over
  // an orange flame is just a paler orange flame.
  return {
    ...style,
    core: ref.paint,
    supersonicCore: shadeHex(ref.paint, 1.4),
    hot: shadeHex(ref.paint, 1.7),
    cool: shadeHex(ref.paint, 0.45),
  };
};

/** The supersonic trail a livery leaves. */
export const trailStyleFor = (livery: CarLivery): TrailStyle => {
  const ref = livery.trail;
  if (!ref) return STOCK_TRAIL;
  const gen = TRAIL_GENERATORS[ref.generator];
  if (!gen) return STOCK_TRAIL;
  const style = gen(ref.params);
  if (ref.paint === null) return style;
  return { ...style, hot: shadeHex(ref.paint, 1.5), cool: ref.paint };
};

/** Everything the material set depends on. Two equal keys share one material set. */
export const materialKey = (livery: CarLivery): string => {
  const finish = finishFor(livery);
  return [
    skinKey(livery),
    livery.team,
    livery.finish?.generator ?? '-',
    finish.map,
    finish.weaveRepeat,
    rimColor(livery, 0).toString(16),
    livery.topper ? attachmentColor(livery, livery.topper).toString(16) : '-',
    livery.antenna ? attachmentColor(livery, livery.antenna).toString(16) : '-',
  ].join('|');
};

export { SKIN, SKIN_DOOR };
